import { Matrix, Vector3, Viewport } from '@babylonjs/core';
import { BaseSystem, SystemOperationDefinition, Zed } from '@modules/core';
import { World } from 'miniplex';

import { Entity as BaseEntity } from '..';
import { TourDanjiCameraAPI } from '../..';

export type ScreenDistanceEntity = {
  ScreenDistanceOptions?: {
    maxDistanceSquared: number;
    position: Vector3;
  };
  ScreenDistance?: {
    position: Vector3;
    distanceSquared: number;
  };
};

type Entity = ScreenDistanceEntity & BaseEntity;

export class ScreenDistanceSystem implements BaseSystem {
  static readonly INJECTABLE_TOKEN = Symbol.for('ScreenDistanceSystem');

  private readonly withScreenDistnaceOptions;
  private readonly world;

  private readonly cameraAPI;
  private readonly screenCenter = new Vector3(0.5, 0.5, 0);

  constructor({
    [Zed.WORLD_TOKEN]: world,
    [TourDanjiCameraAPI.INJECTABLE_TOKEN]: tourDanjiCameraAPI,
  }: {
    [Zed.WORLD_TOKEN]: World<Entity>;
    [TourDanjiCameraAPI.INJECTABLE_TOKEN]: TourDanjiCameraAPI;
  }) {
    this.withScreenDistnaceOptions = world.with('ScreenDistanceOptions', 'TransformNode');
    this.world = world;
    this.cameraAPI = tourDanjiCameraAPI;
  }

  private whenViewMatrixIdle = () => {
    if (this.withScreenDistnaceOptions.size < 1) return;
    const transformMatrix = this.cameraAPI.camera.getTransformationMatrix();
    const viewport = this.cameraAPI.camera.viewport;

    for (const entity of this.withScreenDistnaceOptions) {
      const { ScreenDistanceOptions } = entity;
      const worldPosition = ScreenDistanceOptions.position;
      const distanceSquared = Vector3.DistanceSquared(worldPosition, this.cameraAPI.camera.globalPosition);
      if (distanceSquared > ScreenDistanceOptions.maxDistanceSquared) {
        this.world.removeComponent(entity, 'ScreenDistance');
      } else {
        this.world.update(
          entity,
          'ScreenDistance',
          this.getScreenDistance(worldPosition, { transformMatrix, viewport })
        );
      }
    }
  };

  private getScreenDistance = (
    position: Vector3,
    camera: {
      transformMatrix: Matrix;
      viewport: Viewport;
    }
  ) => {
    const screenPosition = Vector3.Project(position, Matrix.IdentityReadOnly, camera.transformMatrix, camera.viewport);
    screenPosition.z = Math.log1p(100 * screenPosition.z) / Math.log(100);

    const screenDistanceSquared = Vector3.DistanceSquared(screenPosition, this.screenCenter);

    return {
      position: screenPosition,
      distanceSquared: screenDistanceSquared,
    };
  };

  readonly operations: SystemOperationDefinition[] = [
    {
      group: 'viewMatrixIdle',
      run: this.whenViewMatrixIdle,
    },
  ];
}
